/**
 * Branded nominal types for monetary amounts.
 *
 * @CODE:MONEY-PHASE1-301 | SPEC: menu#301 Phase 1 — Branded Money types
 *
 * TypeScript 의 nominal typing 부재를 `__brand` intersection 으로 대체.
 * GrossAmount / NetAmount / TaxAmount 등이 모두 `number` 이지만 *교환 불가능* —
 * `total = net + tax` 같은 의도적 합산만 통과, `subtract(gross, tax)` 같은
 * silent degrade 차단.
 *
 * 불변식 (Phase 3 팩토리에서 강제):
 *   GROSS = NET + TAX
 *   TOTAL = GROSS + TIP - DISCOUNT
 *
 * 본 Phase 1 은 *domain side* branded types 만 도입. wire (BE response)
 * 에서 brand 부여는 Phase 4 (`toOrderHistoryItem(dto)` 단방향 강제) 에서.
 *
 * 사용 가이드:
 * - 새 OrderHistoryItem 생성 시 wire number 를 `as*` 헬퍼 통과 필수
 * - 산술 연산은 raw number 로 unwrap 후 진행 → 결과를 다시 brand
 * - `as*` 헬퍼는 *type assertion only* (런타임 변환 0)
 */

export type GrossAmount = number & { readonly __brand: 'gross' };
export type NetAmount = number & { readonly __brand: 'net' };
export type TaxAmount = number & { readonly __brand: 'tax' };
export type TipAmount = number & { readonly __brand: 'tip' };
export type TotalAmount = number & { readonly __brand: 'total' };

/** wire (BE response number) → domain GrossAmount 변환. Phase 4 에서 boundary 강제. */
export const asGross = (n: number): GrossAmount => n as GrossAmount;
export const asNet = (n: number): NetAmount => n as NetAmount;
export const asTax = (n: number): TaxAmount => n as TaxAmount;
export const asTip = (n: number): TipAmount => n as TipAmount;
export const asTotal = (n: number): TotalAmount => n as TotalAmount;

/**
 * 영수증 금액 팩토리 — 불변식 캡슐화 (#301 Phase 3).
 *
 * @CODE:RECEIPT-FACTORY-301 | SPEC: menu#301 Phase 3 — 불변식 팩토리
 *
 * 불변식:
 *   GROSS = NET + TAX
 *   TOTAL = GROSS + TIP - DISCOUNT
 *
 * 호출처에서 직접 산술 (gross + tax / net + 100 등) 하면 *Branded type 손실* 후
 * raw number 반환 — 컴파일 타임 검증 미작동. 본 팩토리가 *유일한 진입점* 으로
 * 불변식을 한 곳에 가두면 silent regression (gross ≠ net + tax) 차단.
 *
 * 사용 예:
 *   const amounts = makeReceiptAmounts({ net: asNet(100), tax: asTax(16), tip: asTip(10) });
 *   // amounts.grossSubtotal: GrossAmount (= 116)
 *   // amounts.total: TotalAmount (= 126)
 *
 * Phase 4 (wire ↔ domain 경계): 본 팩토리를 wire (BE response) → domain
 * 변환 boundary 의 *유일한 통과 지점* 으로 강제.
 */
export interface ReceiptAmountsInput {
  net: NetAmount;
  tax: TaxAmount;
  tip?: TipAmount;
  /** 포인트 / 쿠폰 할인 (선택) */
  discount?: number;
}

export interface ReceiptAmounts {
  grossSubtotal: GrossAmount;
  netSubtotal: NetAmount;
  tax: TaxAmount;
  tip: TipAmount;
  total: TotalAmount;
}

export function makeReceiptAmounts(input: ReceiptAmountsInput): ReceiptAmounts {
  const tip = input.tip ?? asTip(0);
  const discount = input.discount ?? 0;

  // GROSS = NET + TAX (raw number 산술 후 brand 재부여 — 불변식 캡슐화)
  const grossSubtotal = asGross((input.net as number) + (input.tax as number));
  // TOTAL = GROSS + TIP - DISCOUNT
  const total = asTotal((grossSubtotal as number) + (tip as number) - discount);

  return {
    grossSubtotal,
    netSubtotal: input.net,
    tax: input.tax,
    tip,
    total,
  };
}
